import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import { photoUrl } from "@/lib/api";
import selection from "@/data/photoSelection.json";
import { Reveal } from "@/components/Reveal";
import { GALLERY } from "@/constants/testIds";

const ASPECTS = ["aspect-[4/5]", "aspect-[3/4]", "aspect-[5/4]", "aspect-[4/3]", "aspect-square"];

export const Gallery = ({ preview = false, hideHeader = false }) => {
  const all = selection.gallery;
  const photos = preview ? all.slice(0, 9) : all;
  const [openIndex, setOpenIndex] = useState(null);

  const close = useCallback(() => setOpenIndex(null), []);
  const next = useCallback(
    () => setOpenIndex((i) => (i === null ? null : (i + 1) % photos.length)),
    [photos.length]
  );
  const prev = useCallback(
    () => setOpenIndex((i) => (i === null ? null : (i - 1 + photos.length) % photos.length)),
    [photos.length]
  );

  useEffect(() => {
    if (openIndex === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [openIndex, close, next, prev]);

  return (
    <section id="gallery" data-testid={GALLERY.root} className={hideHeader ? "pb-20 md:pb-28" : "py-24 md:py-32"}>
      <div className="container-editorial">
        {!hideHeader && (
          <Reveal className="max-w-2xl mb-14 md:mb-20">
            <div className="eyebrow-rule">Gallery</div>
            <h2 className="editorial-heading text-3xl md:text-4xl lg:text-[52px]">
              Light, linen,<br /><em>lagoon.</em>
            </h2>
            <p className="mt-6 text-[hsl(var(--muted-foreground))]">
              Tap any image to view full size.
            </p>
          </Reveal>
        )}

        {/* CSS columns masonry */}
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-5">
          {photos.map((id, i) => (
            <button
              type="button"
              key={id}
              data-testid={GALLERY.tile(i)}
              onClick={() => setOpenIndex(i)}
              className={`mb-4 md:mb-5 block w-full break-inside-avoid rounded-2xl overflow-hidden group focus:outline-none focus:ring-2 focus:ring-[hsl(var(--sage-deep))]`}
              aria-label={`Open photo ${i + 1}`}
            >
              <div className={`${ASPECTS[i % ASPECTS.length]} bg-[hsl(var(--marble))] overflow-hidden`}>
                <img
                  src={photoUrl(id)}
                  alt={`Ata House photo ${i + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04] group-hover:opacity-95"
                />
              </div>
            </button>
          ))}
        </div>

        {preview && (
          <div className="mt-14 md:mt-20 flex flex-col items-center text-center">
            <div className="h-px w-16 bg-[hsl(var(--oak-deep))]/40 mb-7" />
            <p className="text-[hsl(var(--muted-foreground))] text-sm mb-6">
              {all.length} photos in the full gallery
            </p>
            <Link
              to="/gallery"
              data-testid="gallery-view-all"
              className="group inline-flex items-center gap-3 px-8 py-3.5 rounded-full border border-[hsl(var(--ink))]/80 text-[hsl(var(--ink))] text-[11px] tracking-[0.32em] uppercase hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--linen))] transition-colors duration-500"
            >
              View the full gallery
              <ArrowUpRight size={15} strokeWidth={1.4} className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {openIndex !== null && (
        <div
          data-testid={GALLERY.lightbox}
          className="fixed inset-0 z-[100] bg-[hsl(var(--ink))]/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
          onClick={close}
        >
          <button
            onClick={(e) => { e.stopPropagation(); close(); }}
            data-testid={GALLERY.lightboxClose}
            aria-label="Close"
            className="absolute top-5 right-5 text-white/90 hover:text-white p-2"
          >
            <X size={28} strokeWidth={1.2} />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            data-testid={GALLERY.lightboxPrev}
            aria-label="Previous"
            className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 text-white/85 hover:text-white p-3"
          >
            <ChevronLeft size={34} strokeWidth={1.2} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            data-testid={GALLERY.lightboxNext}
            aria-label="Next"
            className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 text-white/85 hover:text-white p-3"
          >
            <ChevronRight size={34} strokeWidth={1.2} />
          </button>

          <img
            src={photoUrl(photos[openIndex])}
            alt={`Ata House photo ${openIndex + 1}`}
            className="max-h-[88vh] max-w-[92vw] object-contain rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />

          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/60 text-xs tracking-[0.3em]">
            {openIndex + 1} / {photos.length}
          </div>
        </div>
      )}
    </section>
  );
};
